import { useEffect, useMemo, useState } from 'react';
import {
  Alert,
  AlertIcon,
  Badge,
  Box,
  Button,
  Checkbox,
  Flex,
  Grid,
  HStack,
  Image,
  Input,
  Modal,
  ModalBody,
  ModalContent,
  ModalFooter,
  ModalHeader,
  ModalOverlay,
  Select,
  SimpleGrid,
  Spinner,
  Text,
  VStack,
  useDisclosure,
} from '@chakra-ui/react';
import { useMutation, useQueryClient } from 'react-query';
import { useSearchParams } from 'react-router-dom';
import { useTheme } from '@partiu/ui/contexts/ThemeProvider.tsx';
import { submitFinancialVolumetryChange } from '@partiu/api/dashboard/api.ts';
import {
  useFinancialVolumetryChanges,
  useFinancialVolumetryMonth,
} from '@partiu/api/dashboard/hooks.ts';
import { IFinancialVolumetryRequest } from '@partiu/api/dashboard/types.ts';
import { categoryBadge } from './badges.ts';
import {
  fieldProps,
  FinancialCard,
  FinancialNotice,
  FinancialPagination,
  FinancialShell,
  FinancialTabs,
  financialErrorMessage,
} from './shared.tsx';

const MONTHS = [
  'Janeiro',
  'Fevereiro',
  'Março',
  'Abril',
  'Maio',
  'Junho',
  'Julho',
  'Agosto',
  'Setembro',
  'Outubro',
  'Novembro',
  'Dezembro',
];

const MONTH_SHORT = ['Jan', 'Fev', 'Mar', 'Abr', 'Mai', 'Jun', 'Jul', 'Ago', 'Set', 'Out', 'Nov', 'Dez'];

const STATUS_LABEL = {
  pending: 'Pendente',
  approved: 'Aprovada',
  rejected: 'Rejeitada',
} as const;

type VolumetryView = 'month' | 'requests';
type StatusFilter = 'all' | IFinancialVolumetryRequest['status'];

function periodLabel(year: number, month: number) {
  return `${MONTHS[month - 1]}/${year}`;
}

function shiftMonth(year: number, month: number, delta: number) {
  const index = year * 12 + (month - 1) + delta;
  return { year: Math.floor(index / 12), month: (index % 12) + 1 };
}

function formatCount(value: number) {
  return value.toLocaleString('pt-BR');
}

function formatDate(value: string | null) {
  if (!value) return '—';
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return '—';
  return date.toLocaleString('pt-BR');
}

function remainingLabel(deadlineAt: string | null) {
  if (!deadlineAt) return 'Sem prazo de e-mail';
  const diff = new Date(deadlineAt).getTime() - Date.now();
  if (diff <= 0) return 'Prazo encerrado';
  const days = Math.ceil(diff / 86_400_000);
  return days === 1 ? 'Falta 1 dia' : `Faltam ${days} dias`;
}

function requestKind(item: IFinancialVolumetryRequest) {
  return item.originId || item.repeatUntilMonth ? 'Repetição' : 'Edição';
}

function statusColors(status: IFinancialVolumetryRequest['status']) {
  if (status === 'approved') return { bg: 'green.50', color: 'green.700', border: 'green.100' };
  if (status === 'rejected') return { bg: 'red.50', color: 'red.700', border: 'red.100' };
  return { bg: 'orange.50', color: 'orange.700', border: 'orange.100' };
}

function CategoryMark({ name, rank, size = '36px' }: { name: string; rank?: number; size?: string }) {
  const src = categoryBadge(name, rank);
  if (!src) {
    return (
      <Flex boxSize={size} borderRadius="full" bg="gray.100" align="center" justify="center" flexShrink={0}>
        <Text fontWeight="700" color="gray.500" fontSize="sm">
          {name.trim().slice(0, 1).toUpperCase() || '•'}
        </Text>
      </Flex>
    );
  }
  return <Image src={src} alt="" boxSize={size} objectFit="contain" flexShrink={0} />;
}

function StatusBadge({ status, children }: { status: IFinancialVolumetryRequest['status']; children: string }) {
  const colors = statusColors(status);
  return (
    <Badge
      bg={colors.bg}
      color={colors.color}
      borderWidth="1px"
      borderColor={colors.border}
      borderRadius="full"
      px={2.5}
      py={1}
      textTransform="none"
      fontWeight="700"
      fontSize="xs"
    >
      {children}
    </Badge>
  );
}

function DeltaLabel({ delta }: { delta: number }) {
  if (delta === 0) {
    return (
      <Text fontSize="xs" color="gray.400" fontWeight="700">
        0
      </Text>
    );
  }
  const positive = delta > 0;
  return (
    <Text fontSize="xs" fontWeight="700" color={positive ? 'green.600' : 'red.600'}>
      {positive ? `+${formatCount(delta)}` : formatCount(delta)}
    </Text>
  );
}

function Stat({ label, value, hint }: { label: string; value: string; hint?: string }) {
  return (
    <Box>
      <Text fontSize="xs" fontWeight="700" letterSpacing="0.06em" color="gray.500" textTransform="uppercase">
        {label}
      </Text>
      <Text mt={1} fontSize="lg" fontWeight="700" letterSpacing="-0.02em" lineHeight="1.2">
        {value}
      </Text>
      {hint && (
        <Text mt={1} fontSize="xs" color="gray.500">
          {hint}
        </Text>
      )}
    </Box>
  );
}

function RequestsTab({ primary }: { primary: string }) {
  const [page, setPage] = useState(1);
  const [status, setStatus] = useState<StatusFilter>('all');
  const query = useFinancialVolumetryChanges({ page });
  const loaded = query.data?.results ?? [];
  const results = status === 'all' ? loaded : loaded.filter((item) => item.status === status);
  const total = status === 'all' ? query.data?.count : results.length;

  return (
    <VStack align="stretch" spacing={4}>
      <HStack justify="space-between" align="center" flexWrap="wrap" gap={3}>
        <FinancialTabs
          value={status}
          onChange={(next) => {
            setStatus(next);
            setPage(1);
          }}
          color={primary}
          options={[
            { id: 'all', label: 'Todas' },
            { id: 'pending', label: 'Pendentes' },
            { id: 'approved', label: 'Aprovadas' },
            { id: 'rejected', label: 'Rejeitadas' },
          ]}
        />
        {status !== 'all' && typeof total === 'number' && !query.isLoading && (
          <Text fontSize="sm" color="gray.500" fontWeight="600">
            {formatCount(total)} {total === 1 ? 'solicitação' : 'solicitações'}
          </Text>
        )}
      </HStack>

      {query.isLoading && (
        <FinancialCard p={10}>
          <HStack justify="center">
            <Spinner size="sm" />
            <Text fontSize="sm" color="gray.500">
              Carregando solicitações
            </Text>
          </HStack>
        </FinancialCard>
      )}
      {query.isError && <FinancialNotice tone="error">{financialErrorMessage(query.error)}</FinancialNotice>}

      {!query.isLoading && !query.isError && results.length === 0 && (
        <FinancialNotice>
          {status === 'all' ? 'Nenhuma solicitação ainda.' : 'Nenhuma solicitação com esse status.'}
        </FinancialNotice>
      )}

      {results.map((item) => {
        const colors = statusColors(item.status);
        return (
          <FinancialCard key={item.id}>
            <Box borderLeftWidth="4px" borderLeftColor={colors.border} px={{ base: 4, md: 5 }} py={5}>
              <HStack justify="space-between" align="flex-start" spacing={3}>
                <Box minW={0}>
                  <Text fontSize="xl" fontWeight="700" letterSpacing="-0.03em" lineHeight="1.1">
                    {periodLabel(item.year, item.month)}
                  </Text>
                  <Text mt={1} fontSize="sm" color="gray.500">
                    {requestKind(item)}
                    {item.requestedByName ? ` · ${item.requestedByName}` : ''}
                    {item.createdAt ? ` · ${formatDate(item.createdAt)}` : ''}
                  </Text>
                </Box>
                <StatusBadge status={item.status}>{STATUS_LABEL[item.status]}</StatusBadge>
              </HStack>

              <SimpleGrid columns={{ base: 1, md: 2 }} spacing={2} mt={4}>
                {item.lines.map((line) => {
                  const delta = line.requestedCount - line.officialCount;
                  return (
                    <HStack
                      key={line.levelId}
                      bg="gray.50"
                      borderRadius="xl"
                      px={3}
                      py={2.5}
                      justify="space-between"
                      spacing={3}
                    >
                      <HStack minW={0} spacing={2}>
                        <CategoryMark name={line.levelName} rank={line.levelRank} size="28px" />
                        <Text fontSize="sm" fontWeight="600" noOfLines={1}>
                          {line.levelName}
                        </Text>
                      </HStack>
                      <HStack spacing={2} flexShrink={0}>
                        <Text fontSize="sm" color="gray.500">
                          {formatCount(line.officialCount)}
                        </Text>
                        <Text fontSize="sm" color="gray.300">
                          →
                        </Text>
                        <Text fontSize="sm" fontWeight="700">
                          {formatCount(line.requestedCount)}
                        </Text>
                        <DeltaLabel delta={delta} />
                      </HStack>
                    </HStack>
                  );
                })}
              </SimpleGrid>

              {item.reviewedAt && (
                <Text mt={3} fontSize="sm" color="gray.600">
                  Decisão em {formatDate(item.reviewedAt)}
                </Text>
              )}
              {item.status === 'rejected' && item.rejectionReason && (
                <Box mt={3} bg="red.50" color="red.700" borderRadius="xl" px={3} py={2} fontSize="sm">
                  {item.rejectionReason}
                </Box>
              )}
            </Box>
          </FinancialCard>
        );
      })}

      {!query.isError && status === 'all' && (query.data?.count ?? 0) > 0 && (
        <FinancialPagination
          page={page}
          count={query.data?.count}
          hasPrevious={!!query.data?.previous}
          hasNext={!!query.data?.next}
          isLoading={query.isFetching}
          onPrevious={() => setPage((current) => Math.max(1, current - 1))}
          onNext={() => setPage((current) => current + 1)}
          itemLabel={['solicitação', 'solicitações']}
        />
      )}
    </VStack>
  );
}

export function VolumetryPage() {
  const { colors } = useTheme();
  const primary = colors.primaryColor || '#1a202c';
  const [searchParams, setSearchParams] = useSearchParams();
  const today = new Date();
  const rawYear = Number(searchParams.get('year'));
  const rawMonth = Number(searchParams.get('month'));
  const year = rawYear >= 2000 && rawYear <= 2100 ? rawYear : today.getFullYear();
  const month = rawMonth >= 1 && rawMonth <= 12 ? rawMonth : today.getMonth() + 1;
  const view: VolumetryView = searchParams.get('view') === 'requests' ? 'requests' : 'month';
  const queryClient = useQueryClient();
  const monthQuery = useFinancialVolumetryMonth(year, month);
  const [drafts, setDrafts] = useState<Record<number, string>>({});
  const [repeat, setRepeat] = useState(false);
  const [repeatUntil, setRepeatUntil] = useState('');
  const [feedback, setFeedback] = useState('');
  const [error, setError] = useState('');
  const [refreshing, setRefreshing] = useState(false);
  const confirm = useDisclosure();

  const data = monthQuery.data;
  useEffect(() => {
    const next: Record<number, string> = {};
    data?.categories.forEach((category) => {
      next[category.levelId] = String(category.clientsCount ?? 0);
    });
    setDrafts(next);
    setRepeat(false);
    setRepeatUntil('');
    setError('');
  }, [data?.year, data?.month, data?.categories]);

  const monthsAhead = useMemo(() => {
    return Array.from({ length: 24 }, (_, index) => shiftMonth(year, month, index));
  }, [year, month]);

  const updateParams = (mutate: (params: URLSearchParams) => void) => {
    const params = new URLSearchParams(searchParams);
    mutate(params);
    setSearchParams(params);
  };

  const go = (nextYear: number, nextMonth: number) => {
    updateParams((params) => {
      params.set('year', String(nextYear));
      params.set('month', String(nextMonth));
    });
  };

  const setView = (next: VolumetryView) => {
    updateParams((params) => {
      if (next === 'requests') params.set('view', 'requests');
      else params.delete('view');
    });
  };

  const refresh = async () => {
    setRefreshing(true);
    try {
      await Promise.all([
        queryClient.invalidateQueries('financial-volumetry-month'),
        queryClient.invalidateQueries('financial-volumetry-changes'),
      ]);
    } finally {
      setRefreshing(false);
    }
  };

  const previous = shiftMonth(year, month, -1);
  const next = shiftMonth(year, month, 1);
  const categories = data?.categories ?? [];
  const officialTotal = categories.reduce((sum, category) => sum + (category.clientsCount || 0), 0);
  const draftTotal = categories.reduce((sum, category) => sum + Number(drafts[category.levelId] || 0), 0);
  const changed = categories.filter((category) => Number(drafts[category.levelId] || 0) !== (category.clientsCount || 0));
  const pending = data?.pending;
  const pendingTotal = pending?.lines.reduce((sum, line) => sum + line.requestedCount, 0) ?? 0;

  const submit = useMutation(
    () => {
      const [untilYear, untilMonth] = repeatUntil.split('-').map(Number);
      return submitFinancialVolumetryChange({
        year,
        month,
        lines: categories.map((category) => ({
          levelId: category.levelId,
          clientsCount: Number(drafts[category.levelId] ?? 0),
        })),
        repeatUntilYear: repeat ? untilYear : undefined,
        repeatUntilMonth: repeat ? untilMonth : undefined,
      });
    },
    {
      onSuccess: (result) => {
        const skipped = result.skipped?.length
          ? ` Meses não alterados: ${result.skipped.map((item) => `${periodLabel(item.year, item.month)} (${item.reason})`).join('; ')}.`
          : '';
        setFeedback(
          `Solicitação enviada para ${result.created.length} ${result.created.length === 1 ? 'mês' : 'meses'}. Os números oficiais continuam os mesmos até a aprovação.${skipped}`
        );
        setError('');
        confirm.onClose();
        setView('requests');
        queryClient.invalidateQueries('financial-volumetry-month');
        queryClient.invalidateQueries('financial-volumetry-changes');
      },
      onError: (caught) => {
        setError(financialErrorMessage(caught));
        confirm.onClose();
      },
    }
  );

  const statusText = data?.locked
    ? 'Aprovada e bloqueada'
    : pending
      ? 'Alteração pendente'
      : data?.blockMessage || 'Edição disponível';
  const proposalValue = data?.editable
    ? formatCount(draftTotal)
    : pending
      ? formatCount(pendingTotal)
      : '—';
  const proposalHint = data?.editable
    ? changed.length === 0
      ? 'Nenhuma categoria alterada'
      : `${changed.length} ${changed.length === 1 ? 'categoria alterada' : 'categorias alteradas'}`
    : pending
      ? 'Aguardando decisão'
      : undefined;

  return (
    <FinancialShell
      title="Volumetria"
      description="Consulte o mês, envie a solicitação e acompanhe a decisão da equipe Partiu."
      maxW="1180px"
    >
      <HStack justify="space-between" align="center" flexWrap="wrap" gap={3}>
        <FinancialTabs
          value={view}
          onChange={setView}
          color={primary}
          options={[
            { id: 'month', label: 'Mês' },
            { id: 'requests', label: 'Solicitações' },
          ]}
        />
        <Button
          size="sm"
          variant="outline"
          borderRadius="full"
          isLoading={refreshing}
          onClick={() => {
            void refresh();
          }}
        >
          Atualizar
        </Button>
      </HStack>

      {feedback && (
        <Alert status="success" borderRadius="xl">
          <AlertIcon />
          {feedback}
        </Alert>
      )}
      {error && (
        <Alert status="error" borderRadius="xl">
          <AlertIcon />
          {error}
        </Alert>
      )}

      {view === 'requests' ? (
        <RequestsTab primary={primary} />
      ) : (
        <VStack align="stretch" spacing={4}>
          <FinancialCard p={{ base: 4, md: 6 }}>
            <HStack justify="space-between" align="flex-start" flexWrap="wrap" gap={4}>
              <Box>
                <Text fontSize="xs" fontWeight="700" letterSpacing="0.08em" color="gray.500" textTransform="uppercase">
                  Mês de referência
                </Text>
                <Text fontSize={{ base: '2xl', md: '3xl' }} fontWeight="700" letterSpacing="-0.04em" lineHeight="1.05" mt={1}>
                  {periodLabel(year, month)}
                </Text>
              </Box>
              <HStack>
                <Button
                  size="sm"
                  variant="outline"
                  borderRadius="full"
                  onClick={() => go(previous.year, previous.month)}
                >
                  Anterior
                </Button>
                <Button size="sm" variant="outline" borderRadius="full" onClick={() => go(next.year, next.month)}>
                  Próximo
                </Button>
              </HStack>
            </HStack>

            <Grid templateColumns={{ base: 'repeat(4, 1fr)', md: 'repeat(12, 1fr)' }} gap={2} mt={5}>
              {MONTH_SHORT.map((name, index) => {
                const selected = index + 1 === month;
                return (
                  <Button
                    key={name}
                    size="sm"
                    borderRadius="full"
                    variant="outline"
                    borderColor={selected ? primary : 'gray.200'}
                    bg={selected ? primary : 'white'}
                    color={selected ? 'white' : 'gray.600'}
                    fontWeight="600"
                    px={0}
                    _hover={{ bg: selected ? primary : 'gray.50' }}
                    onClick={() => go(year, index + 1)}
                  >
                    {name}
                  </Button>
                );
              })}
            </Grid>

            <SimpleGrid columns={{ base: 1, md: 3 }} spacing={6} mt={6} pt={5} borderTopWidth="1px" borderColor="gray.100">
              <Stat
                label="Situação"
                value={data ? statusText : '—'}
                hint={
                  data?.locked
                    ? 'Este mês não aceita nova edição.'
                    : pending
                      ? 'Aguardando a equipe Partiu.'
                      : data?.editable
                        ? 'Você pode enviar uma solicitação.'
                        : undefined
                }
              />
              <Stat label="Total oficial" value={data ? formatCount(officialTotal) : '—'} hint={data ? `${categories.length} ${categories.length === 1 ? 'categoria' : 'categorias'}` : undefined} />
              <Stat
                label={data?.editable ? 'Nova proposta' : 'Solicitado'}
                value={data ? proposalValue : '—'}
                hint={data?.deadlineAt && data.editable ? `${remainingLabel(data.deadlineAt)} · até ${formatDate(data.deadlineAt)}` : proposalHint}
              />
            </SimpleGrid>
          </FinancialCard>

          {monthQuery.isLoading && (
            <FinancialCard p={10}>
              <HStack justify="center">
                <Spinner size="sm" />
                <Text fontSize="sm" color="gray.500">
                  Carregando a volumetria
                </Text>
              </HStack>
            </FinancialCard>
          )}
          {monthQuery.isError && <FinancialNotice tone="error">{financialErrorMessage(monthQuery.error)}</FinancialNotice>}

          {data?.emailSentAt && (
            <Text fontSize="sm" color="gray.500" px={1}>
              E-mail enviado em {formatDate(data.emailSentAt)}
            </Text>
          )}

          {data && (
            <FinancialCard>
              <HStack px={{ base: 4, md: 5 }} py={4} justify="space-between" borderBottomWidth="1px" borderColor="gray.100">
                <Box>
                  <Text fontWeight="700">Categorias</Text>
                  <Text fontSize="sm" color="gray.500">
                    {data.editable ? 'Ajuste a quantidade e envie para aprovação.' : 'Números oficiais deste mês.'}
                  </Text>
                </Box>
              </HStack>
              {categories.length === 0 ? (
                <Text px={5} py={8} color="gray.500" fontSize="sm">
                  Nenhuma categoria neste mês.
                </Text>
              ) : (
                categories.map((category, index) => {
                  const requested = pending?.lines.find((line) => line.levelId === category.levelId);
                  const draftValue = drafts[category.levelId] ?? '';
                  const delta = data.editable
                    ? Number(draftValue || 0) - category.clientsCount
                    : requested
                      ? requested.requestedCount - category.clientsCount
                      : 0;
                  return (
                    <HStack
                      key={category.levelId}
                      px={{ base: 4, md: 5 }}
                      py={4}
                      spacing={4}
                      align="center"
                      borderTopWidth={index === 0 ? 0 : '1px'}
                      borderColor="gray.100"
                    >
                      <CategoryMark name={category.levelName} rank={category.levelRank} />
                      <Box flex="1" minW={0}>
                        <Text fontWeight="700" noOfLines={1}>
                          {category.levelName}
                        </Text>
                        <Text fontSize="sm" color="gray.500">
                          Oficial {formatCount(category.clientsCount)}
                        </Text>
                      </Box>
                      {data.editable ? (
                        <Box flexShrink={0} textAlign="right">
                          <Text fontSize="xs" color="gray.500" mb={1} fontWeight="600">
                            Nova quantidade
                          </Text>
                          <HStack justify="flex-end" spacing={2}>
                            <Input
                              {...fieldProps}
                              w="112px"
                              textAlign="right"
                              inputMode="numeric"
                              isDisabled={submit.isLoading}
                              value={draftValue}
                              onChange={(event) =>
                                setDrafts((current) => ({
                                  ...current,
                                  [category.levelId]: event.target.value.replace(/\D/g, ''),
                                }))
                              }
                            />
                            <Box minW="42px">
                              <DeltaLabel delta={delta} />
                            </Box>
                          </HStack>
                        </Box>
                      ) : (
                        <Box flexShrink={0} textAlign="right">
                          <Text fontSize="xs" color="gray.500" fontWeight="600">
                            Solicitado
                          </Text>
                          <HStack justify="flex-end" spacing={2} mt={1}>
                            <Text fontWeight="700" color={requested ? 'gray.800' : 'gray.400'}>
                              {requested ? formatCount(requested.requestedCount) : '—'}
                            </Text>
                            {requested && <DeltaLabel delta={delta} />}
                          </HStack>
                        </Box>
                      )}
                    </HStack>
                  );
                })
              )}
            </FinancialCard>
          )}

          {data?.editable && (
            <FinancialCard p={{ base: 4, md: 5 }}>
              <VStack align="stretch" spacing={4}>
                <Checkbox isChecked={repeat} onChange={(event) => setRepeat(event.target.checked)}>
                  Repetir estes valores nos meses seguintes
                </Checkbox>
                {repeat && (
                  <Select
                    {...fieldProps}
                    maxW="320px"
                    value={repeatUntil}
                    onChange={(event) => setRepeatUntil(event.target.value)}
                  >
                    <option value="">Selecione até qual mês</option>
                    {monthsAhead.map((item) => (
                      <option key={`${item.year}-${item.month}`} value={`${item.year}-${item.month}`}>
                        {periodLabel(item.year, item.month)}
                      </option>
                    ))}
                  </Select>
                )}
                <Text fontSize="sm" color="gray.500">
                  Cada mês gera uma solicitação própria. Aprovar ou rejeitar um mês não muda os outros.
                </Text>
                <HStack justify="space-between" align="center" flexWrap="wrap" gap={3}>
                  <Text fontSize="sm" color="gray.600" fontWeight="600">
                    {changed.length === 0
                      ? 'Nenhuma categoria diferente do oficial.'
                      : `${changed.length} ${changed.length === 1 ? 'categoria será enviada com valor novo' : 'categorias serão enviadas com valor novo'}.`}
                  </Text>
                  <Button
                    borderRadius="full"
                    px={6}
                    bg={primary}
                    color="white"
                    _hover={{ opacity: 0.92 }}
                    isDisabled={repeat && !repeatUntil}
                    onClick={confirm.onOpen}
                  >
                    Enviar solicitação
                  </Button>
                </HStack>
              </VStack>
            </FinancialCard>
          )}
        </VStack>
      )}

      <Modal isOpen={confirm.isOpen} onClose={confirm.onClose} isCentered>
        <ModalOverlay />
        <ModalContent borderRadius="2xl">
          <ModalHeader letterSpacing="-0.02em">Enviar solicitação</ModalHeader>
          <ModalBody>
            <Text color="gray.600">
              A volumetria oficial de {periodLabel(year, month)}
              {repeat && repeatUntil
                ? ` até ${periodLabel(Number(repeatUntil.split('-')[0]), Number(repeatUntil.split('-')[1]))}`
                : ''}{' '}
              não muda até a aprovação. Uma solicitação já enviada não pode ser editada.
            </Text>
            {changed.length > 0 && (
              <VStack align="stretch" spacing={2} mt={4}>
                {changed.map((category) => (
                  <HStack key={category.levelId} justify="space-between" bg="gray.50" borderRadius="lg" px={3} py={2}>
                    <Text fontSize="sm" fontWeight="600">
                      {category.levelName}
                    </Text>
                    <Text fontSize="sm">
                      {formatCount(category.clientsCount)} → {formatCount(Number(drafts[category.levelId] || 0))}
                    </Text>
                  </HStack>
                ))}
              </VStack>
            )}
          </ModalBody>
          <ModalFooter gap={2}>
            <Button variant="ghost" borderRadius="full" onClick={confirm.onClose}>
              Cancelar
            </Button>
            <Button
              borderRadius="full"
              bg={primary}
              color="white"
              _hover={{ opacity: 0.92 }}
              isLoading={submit.isLoading}
              onClick={() => submit.mutate()}
            >
              Confirmar envio
            </Button>
          </ModalFooter>
        </ModalContent>
      </Modal>
    </FinancialShell>
  );
}
